import { useState, useRef, useEffect, useCallback } from 'react'
import { CommandParser } from '@terminal/CommandParser'
import { commandRegistry } from '@terminal/commands'
import { TypedStorage } from '../utils/storage'
import CommandHistoryEntry from './CommandHistoryEntry'
import TerminalInput from './TerminalInput'
import type { CommandEntry, SessionNode } from '../types/commands'

interface TerminalProps {
  onThemeChange?: (theme: 'professional' | 'horror' | 'tandy') => void
  onPlayAudio?: () => void
}

const Terminal = ({ onThemeChange, onPlayAudio }: TerminalProps) => {
  const [history, setHistory] = useState<CommandEntry[]>([])
  const [isProcessing, setIsProcessing] = useState(false)
  const [currentDirectory, setCurrentDirectory] = useState('~')
  const [sessionFilesystem, setSessionFilesystem] = useState<SessionNode[]>([])
  const [srAnnouncement, setSrAnnouncement] = useState('')

  const lastEntryRef = useRef<HTMLDivElement>(null)
  const parserRef = useRef(new CommandParser(commandRegistry))
  const dirPersistRef = useRef<ReturnType<typeof setTimeout>>(undefined)
  const fsPersistRef = useRef<ReturnType<typeof setTimeout>>(undefined)

  // Refs to hold latest values so callbacks don't need them as deps
  const currentDirectoryRef = useRef(currentDirectory)
  const sessionFilesystemRef = useRef(sessionFilesystem)
  const onThemeChangeRef = useRef(onThemeChange)
  const onPlayAudioRef = useRef(onPlayAudio)

  currentDirectoryRef.current = currentDirectory
  sessionFilesystemRef.current = sessionFilesystem
  onThemeChangeRef.current = onThemeChange
  onPlayAudioRef.current = onPlayAudio

  // Auto-scroll using ref on last entry
  useEffect(() => {
    if (history.length > 0) {
      const timer = setTimeout(() => {
        lastEntryRef.current?.scrollIntoView({ behavior: 'auto', block: 'nearest' })
      }, 0)
      return () => clearTimeout(timer)
    }
  }, [history.length])

  // Load persisted state on mount
  useEffect(() => {
    const dir = TypedStorage.get('terminal-current-directory', '~')
    const fs = TypedStorage.get<SessionNode[]>('terminal-session-filesystem', [])
    setCurrentDirectory(dir)
    setSessionFilesystem(fs)
    currentDirectoryRef.current = dir
    sessionFilesystemRef.current = fs
  }, [])

  // Debounced persistence - current directory
  useEffect(() => {
    clearTimeout(dirPersistRef.current)
    dirPersistRef.current = setTimeout(() => {
      TypedStorage.set('terminal-current-directory', currentDirectory)
    }, 500)
    return () => clearTimeout(dirPersistRef.current)
  }, [currentDirectory])

  // Debounced persistence - session filesystem
  useEffect(() => {
    clearTimeout(fsPersistRef.current)
    fsPersistRef.current = setTimeout(() => {
      TypedStorage.set('terminal-session-filesystem', sessionFilesystem)
    }, 500)
    return () => clearTimeout(fsPersistRef.current)
  }, [sessionFilesystem])

  // Stable callback - never changes identity (reads from refs)
  const handleExecute = useCallback(async (input: string) => {
    setIsProcessing(true)
    setSrAnnouncement('Executing command')

    const output = await parserRef.current.execute(input, {
      currentDirectory: currentDirectoryRef.current,
      setCurrentDirectory: (dir: string) => {
        setCurrentDirectory(dir)
        currentDirectoryRef.current = dir
      },
      sessionFilesystem: sessionFilesystemRef.current,
      setSessionFilesystem: (fs: SessionNode[]) => {
        setSessionFilesystem(fs)
        sessionFilesystemRef.current = fs
      },
    })

    // Handle clear command
    if (output[0]?.content === '__CLEAR__') {
      setHistory([])
      setIsProcessing(false)
      return
    }

    // Handle theme change
    if (typeof output[0]?.content === 'string' && output[0].content.startsWith('__THEME_')) {
      const theme = output[0].content.replace(/^__THEME_/, '').replace(/__$/, '').toLowerCase() as 'professional' | 'horror' | 'tandy'
      onThemeChangeRef.current?.(theme)
      const entry: CommandEntry = {
        input,
        output: [{ type: 'success', content: `Theme changed to: ${theme}` }],
        timestamp: new Date(),
      }
      setHistory((prev) => [...prev, entry])
      setSrAnnouncement(`Theme changed to ${theme} mode`)
      setIsProcessing(false)
      return
    }

    // Handle audio play
    if (typeof output[0]?.content === 'string' && output[0].content === '__PLAY_AUDIO__') {
      onPlayAudioRef.current?.()
      const entry: CommandEntry = {
        input,
        output: [{ type: 'success', content: 'Now playing: twelveson.mp3' }],
        timestamp: new Date(),
      }
      setHistory((prev) => [...prev, entry])
      setSrAnnouncement('Playing background music')
      setIsProcessing(false)
      return
    }

    // Standard command result
    const entry: CommandEntry = {
      input,
      output,
      timestamp: new Date(),
    }
    setHistory((prev) => [...prev, entry])

    const hasError = output.some(o => o.type === 'error')
    const hasSuccess = output.some(o => o.type === 'success')
    if (hasError) {
      setSrAnnouncement('Command resulted in an error')
    } else if (hasSuccess) {
      setSrAnnouncement('Command completed successfully')
    } else {
      setSrAnnouncement('Command completed')
    }

    setIsProcessing(false)
  }, []) // Empty deps - reads everything from refs

  // Stable callback - never changes identity
  const getAutocomplete = useCallback((partial: string) => {
    return parserRef.current.getAutocomplete(partial, currentDirectoryRef.current)
  }, [])

  const addHistoryEntry = useCallback((entry: CommandEntry) => {
    setHistory((prev) => [...prev, entry])
  }, [])

  return (
    <div
      className="terminal-interface"
      onClick={() => document.getElementById('terminal-input')?.focus()}
      role="region"
      aria-label="Terminal command interface"
    >
      {/* Command History */}
      <div
        className="command-history"
        aria-label="Command history output"
      >
        {history.map((entry, i) => (
          <CommandHistoryEntry
            key={entry.timestamp.getTime()}
            entry={entry}
            ref={i === history.length - 1 ? lastEntryRef : undefined}
          />
        ))}
      </div>

      {/* Loading Indicator */}
      {isProcessing && (
        <div className="loading-indicator" role="status" aria-live="polite">
          <span className="loading-spinner" aria-hidden="true">⏳</span>
          <span className="loading-text">Executing command, please wait...</span>
        </div>
      )}

      {/* Command Input - isolated component, typing only re-renders this */}
      <TerminalInput
        onExecute={handleExecute}
        getAutocomplete={getAutocomplete}
        addHistoryEntry={addHistoryEntry}
        isProcessing={isProcessing}
        currentDirectory={currentDirectory}
      />

      {/* Screen reader announcements */}
      <div
        role="status"
        aria-live="assertive"
        aria-atomic="true"
        className="sr-only"
      >
        {srAnnouncement}
      </div>
    </div>
  )
}

export default Terminal
